/**
 * API client with typed endpoints.
 * Integrates with the Darwin Resty backend.
 */

import axios, { AxiosInstance } from 'axios';
import type { EntityType, Case, CaseStatus, CaseStatistics, DocumentStatus, RedactionReason } from '../types';

// Types
export interface User {
  id: string;
  email: string;
  first_name?: string;
  last_name?: string;
  status?: string;
  roles?: string[];
  tenant_id?: string;
  current_tenant?: {
    id: string;
    name: string;
    roles: string[];
  };
  created_at?: string;
  updated_at?: string;
  last_login?: string;
}

export interface LoginRequest {
  email: string;
  password: string;
}

// Darwin format access token object
export interface AccessTokenObject {
  token: string;
  exp: string;
  roles?: string[];
  tenant_id?: string;
}

export interface LoginResponse {
  access_token: string | AccessTokenObject;
  refresh_token: string;
  user: User;
}

// Document types
export interface Document {
  id: string;
  name: string;
  type: string;
  upload_date: string;
  updated_at: string;
  uploaded_by: string;
  tenant_id: string;
  original_path: string;
  redacted_path?: string;
  file_size: number;
  page_count?: number;
  status: 'unprocessed' | 'needs_redaction' | 'redacted' | 'completed';
  metadata?: Record<string, any>;
  error_message?: string;
  case_id?: string;
}

// API returns the document directly with a message field, not wrapped
export interface DocumentUploadResponse extends Document {
  message: string;
}

export interface GetDocumentsParams {
  status?: string;
  limit?: number;
  offset?: number;
  search?: string;
  file_type?: string; // Comma-separated MIME types
}

export interface DocumentListResponse {
  documents: Document[];
  total: number;
  limit: number;
  offset: number;
}

export interface ChunkedUploadInitRequest {
  filename: string;
  file_size: number;
  file_type: string;
  total_chunks: number;
  metadata?: Record<string, any>;
  case_id?: string;
}

export interface ChunkedUploadInitResponse {
  upload_id: string;
  message: string;
}

export interface ChunkedUploadStatusResponse {
  upload_id: string;
  filename: string;
  total_chunks: number;
  chunks_received: number[];
  progress: number;
  completed: boolean;
}

export interface ChunkedUploadCompleteResponse {
  document: Document;
  message: string;
}

// Unified HTML content types for all document types (DOCX, EML, MSG, TXT, etc.)
export interface HtmlContent {
  html: string;
  title: string | null;
  metadata: Record<string, unknown>;
  success: boolean;
  error_message?: string;
}

// Create axios instance
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:7773';
const API_PREFIX = import.meta.env.VITE_API_PREFIX || 'launchpad/redactit/api';

export const api: AxiosInstance = axios.create({
  baseURL: `${API_BASE_URL}/${API_PREFIX}`,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// API endpoints
export const authApi = {
  // Authentication
  login: async (data: LoginRequest): Promise<LoginResponse> => {
    const response = await api.post('/auth/login', data);
    return response.data;
  },

  getCurrentUser: async (): Promise<User> => {
    const response = await api.get('/auth/me');
    return response.data;
  },

  refreshToken: async (data: { access_token: string; refresh_token: string }): Promise<any> => {
    const response = await api.post('/auth/refresh', data);
    return response.data;
  },

  getPermissions: async (): Promise<any> => {
    const response = await api.get('/auth/permissions');
    return response.data;
  },

  updateProfile: async (data: Partial<User>): Promise<User> => {
    const response = await api.put('/auth/profile', data);
    return response.data;
  },

  changePassword: async (data: { current_password: string; new_password: string }): Promise<void> => {
    await api.post('/auth/change-password', data);
  },
};

export const documentsApi = {
  // Upload a single file (for files <= 100MB)
  uploadFile: async (
    file: File,
    options?: {
      metadata?: Record<string, any>;
      case_id?: string;
      onUploadProgress?: (progressEvent: any) => void;
    }
  ): Promise<DocumentUploadResponse> => {
    const formData = new FormData();
    formData.append('file', file);
    if (options?.metadata) {
      formData.append('metadata', JSON.stringify(options.metadata));
    }

    // Build URL with case_id query param if provided
    let url = '/documents/upload';
    if (options?.case_id) {
      url += `?case_id=${encodeURIComponent(options.case_id)}`;
    }

    const response = await api.post(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: options?.onUploadProgress,
    });
    return response.data;
  },

  // Get all documents with optional filters
  getDocuments: async (params?: GetDocumentsParams): Promise<DocumentListResponse> => {
    const response = await api.get('/documents', { params });
    return response.data;
  },

  // Delete a document
  deleteDocument: async (documentId: string): Promise<{ message: string }> => {
    const response = await api.delete(`/documents/${documentId}`);
    return response.data;
  },

  // Download a document
  downloadDocument: async (
    documentId: string,
    version: 'original' | 'redacted' = 'original',
    fallbackFilename?: string
  ): Promise<{ blob: Blob; filename: string }> => {
    const response = await api.get(`/documents/${documentId}/download`, {
      params: { version },
      responseType: 'blob',
    });

    // Extract filename from Content-Disposition header
    const contentDisposition = response.headers['content-disposition'];
    let filename = fallbackFilename || 'download';
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="?([^";\n]+)"?/);
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1];
      }
    }

    return { blob: response.data, filename };
  },

  // Get HTML content for any document type (DOCX, EML, MSG, TXT, etc.)
  getHtmlContent: async (documentId: string): Promise<HtmlContent> => {
    const response = await api.get(`/documents/${documentId}/html-content`);
    return response.data;
  },

  // Chunked upload - Initialize
  initChunkedUpload: async (data: ChunkedUploadInitRequest): Promise<ChunkedUploadInitResponse> => {
    const response = await api.post('/documents/chunked-upload/init', data);
    return response.data;
  },

  // Chunked upload - Upload a chunk
  uploadChunk: async (
    uploadId: string,
    chunkNumber: number,
    chunkData: Blob,
    checksum?: string,
    onUploadProgress?: (progressEvent: any) => void
  ): Promise<{ message: string }> => {
    const formData = new FormData();
    formData.append('chunk', chunkData);
    if (checksum) {
      formData.append('checksum', checksum);
    }

    const response = await api.post(
      `/documents/chunked-upload/${uploadId}/chunk/${chunkNumber}`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress,
      }
    );
    return response.data;
  },

  // Chunked upload - Get status
  getChunkedUploadStatus: async (uploadId: string): Promise<ChunkedUploadStatusResponse> => {
    const response = await api.get(`/documents/chunked-upload/${uploadId}/status`);
    return response.data;
  },

  // Chunked upload - Complete
  completeChunkedUpload: async (uploadId: string): Promise<ChunkedUploadCompleteResponse> => {
    const response = await api.post(`/documents/chunked-upload/${uploadId}/complete`);
    return response.data;
  },
};

// Health check endpoint
export const healthApi = {
  check: async (): Promise<{ status: string; timestamp: string }> => {
    const response = await axios.get(`${API_BASE_URL}/health`);
    return response.data;
  },
};

// Redaction types
export interface BoundingBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface ManualRedaction {
  id: string;
  type: string;
  page: number;
  bounds: BoundingBox;
  color?: string;
  opacity?: number;
}

export interface ApplyRedactionsRequest {
  document_id: string;
  redactions: ManualRedaction[];
  create_new_version?: boolean;
}

export interface ApplyRedactionsResponse {
  document_id: string;
  redacted_document_id: string;
  redactions_applied: number;
  status: string;
  message: string;
}

export type RedactionMethod = 'blackout' | 'blur' | 'replace' | 'remove';

// Redaction job types
export interface RedactionJobRequest {
  document_id: string;
  entity_types?: EntityType[];
  redaction_method?: RedactionMethod;
  auto_approve?: boolean;
  custom_patterns?: string[];
}

export interface RedactionJobResponse {
  job_id: string;
  document_id: string;
  status: string;
  created_at: string;
  entity_types: EntityType[];
  redaction_method: RedactionMethod;
  message: string;
}

// Container redaction response (for PST files that create jobs for all children)
export interface ContainerRedactionJobInfo {
  job_id: string;
  document_id: string;
  document_name: string;
  entities_detected: number;
}

export interface ContainerRedactionResponse {
  container_id: string;
  jobs_created: number;
  jobs: ContainerRedactionJobInfo[];
  message: string;
}

// Union type for createJob response
export type CreateJobResponse = RedactionJobResponse | ContainerRedactionResponse;

// Type guard to check if response is a container response
export function isContainerRedactionResponse(
  response: CreateJobResponse
): response is ContainerRedactionResponse {
  return 'container_id' in response;
}

export interface RedactionJobStatus {
  job_id: string;
  document_id: string;
  status: string;
  progress: number;
  entities_detected: number;
  entities_approved: number;
  entities_rejected: number;
  created_at: string;
  updated_at: string;
  completed_at?: string;
  error_message?: string;
}

// Entity suggestion from AI detection
export interface EntitySuggestion {
  index: number;
  entity_type: EntityType;
  text: string;
  confidence: number;
  page: number;
  bounds?: BoundingBox;
  replacement_text?: string;
  status: 'pending' | 'approved' | 'rejected';
}

export interface EntitySuggestionsResponse {
  job_id: string;
  document_id: string;
  suggestions: EntitySuggestion[];
  total: number;
  pending_count: number;
  approved_count: number;
  rejected_count: number;
  job_status: string;
}

export interface ApprovalRequest {
  approved_entities: number[];
  rejected_entities?: number[];
  entity_reasons: Record<number, string>;  // Map entity index -> reason text (required)
  apply_redactions?: boolean;
}

export interface ApprovalResponse {
  job_id: string;
  approved_count: number;
  rejected_count: number;
  status: string;
  message: string;
  redacted_document_id?: string;
}

export interface ApplyJobRedactionsResponse {
  job_id: string;
  document_id: string;
  redacted_document_id: string | null;
  redactions_applied: number;
  status: string;
  message: string;
}

// Redaction API endpoints
export const redactionApi = {
  // Create a new redaction job (triggers AI detection)
  // For container documents (PST), returns ContainerRedactionResponse with jobs for all children
  // For regular documents, returns RedactionJobResponse with a single job
  createJob: async (request: RedactionJobRequest): Promise<CreateJobResponse> => {
    const response = await api.post('/redact/', request);
    return response.data;
  },

  // Get redaction job status
  getJobStatus: async (jobId: string): Promise<RedactionJobStatus> => {
    const response = await api.get(`/redact/${jobId}`);
    return response.data;
  },

  // Get entity suggestions with bounding boxes for PDF display
  getSuggestions: async (jobId: string, includeRejected: boolean = false): Promise<EntitySuggestionsResponse> => {
    const response = await api.get(`/redact/${jobId}/suggestions`, {
      params: { include_rejected: includeRejected }
    });
    return response.data;
  },

  // Approve or reject entity suggestions
  approveSuggestions: async (jobId: string, approval: ApprovalRequest): Promise<ApprovalResponse> => {
    const response = await api.put(`/redact/${jobId}/approve`, approval);
    return response.data;
  },

  // Cancel a redaction job
  cancelJob: async (jobId: string): Promise<void> => {
    await api.delete(`/redact/${jobId}`);
  },

  // Get all redaction jobs for the tenant
  getJobs: async (params?: {
    document_id?: string;
    status?: string;
    limit?: number;
    offset?: number;
  }): Promise<RedactionJobStatus[]> => {
    const response = await api.get('/redact/', { params });
    return response.data;
  },

  // Apply manual redactions to a document (PDF only, with coordinates)
  applyRedactions: async (request: ApplyRedactionsRequest): Promise<ApplyRedactionsResponse> => {
    const response = await api.post('/redact/apply', request);
    return response.data;
  },

  // Apply approved redactions from a job to create a redacted document
  // Works for all file types - auto-detects method based on document type
  applyJobRedactions: async (jobId: string): Promise<ApplyJobRedactionsResponse> => {
    const response = await api.post(`/redact/${jobId}/apply`);
    return response.data;
  },
};

// Case types
export interface CreateCaseRequest {
  name: string;
  notes?: string;
}

export interface UpdateCaseRequest {
  name?: string;
  notes?: string;
}

export interface CaseListResponse {
  cases: Case[];
  total: number;
  limit: number;
  offset: number;
}

export interface GetCasesParams {
  status?: CaseStatus;
  owner?: string;
  search?: string;
  limit?: number;
  offset?: number;
}

export interface CaseWithDocumentsResponse extends Case {
  documents: Document[];
}

export interface AddDocumentsToCaseRequest {
  document_ids: string[];
}

export interface UpdateDocumentStatusRequest {
  status: DocumentStatus;
}

// Cases API endpoints
export const casesApi = {
  // Create a new case
  createCase: async (data: CreateCaseRequest): Promise<Case> => {
    const response = await api.post('/cases/', data);
    return response.data;
  },

  // Get all cases with optional filters
  getCases: async (params?: GetCasesParams): Promise<CaseListResponse> => {
    const response = await api.get('/cases/', { params });
    return response.data;
  },

  // Get a specific case by ID with its documents
  getCase: async (caseId: string): Promise<CaseWithDocumentsResponse> => {
    const response = await api.get(`/cases/${caseId}`);
    return response.data;
  },

  // Update a case
  updateCase: async (caseId: string, data: UpdateCaseRequest): Promise<Case> => {
    const response = await api.put(`/cases/${caseId}`, data);
    return response.data;
  },

  // Delete a case (soft delete)
  deleteCase: async (caseId: string): Promise<{ message: string }> => {
    const response = await api.delete(`/cases/${caseId}`);
    return response.data;
  },

  // Add documents to a case
  addDocuments: async (caseId: string, documentIds: string[]): Promise<{ message: string; added_count: number }> => {
    const response = await api.post(`/cases/${caseId}/documents`, { document_ids: documentIds });
    return response.data;
  },

  // Remove a document from a case
  removeDocument: async (caseId: string, documentId: string): Promise<{ message: string }> => {
    const response = await api.delete(`/cases/${caseId}/documents/${documentId}`);
    return response.data;
  },

  // Update a document's status within a case
  updateDocumentStatus: async (
    caseId: string,
    documentId: string,
    status: DocumentStatus
  ): Promise<Document> => {
    const response = await api.put(`/cases/${caseId}/documents/${documentId}/status`, { status });
    return response.data;
  },

  // Finalize a document (mark as completed)
  finalizeDocument: async (caseId: string, documentId: string): Promise<Document> => {
    const response = await api.post(`/cases/${caseId}/documents/${documentId}/finalize`);
    return response.data;
  },

  // Get case statistics
  getCaseStatistics: async (caseId: string): Promise<CaseStatistics> => {
    const response = await api.get(`/cases/${caseId}/statistics`);
    return response.data;
  },
};

// Reason types
export interface CreateReasonRequest {
  title: string;
  description?: string;
}

export interface UpdateReasonRequest {
  title?: string;
  description?: string;
}

export interface ReasonListResponse {
  reasons: RedactionReason[];
  total: number;
  limit: number;
  offset: number;
}

export interface GetReasonsParams {
  limit?: number;
  offset?: number;
}

// Reasons API endpoints
export const reasonsApi = {
  // Create a new reason
  createReason: async (data: CreateReasonRequest): Promise<RedactionReason> => {
    const response = await api.post('/reasons/', data);
    return response.data;
  },

  // Get all reasons for the tenant
  getReasons: async (params?: GetReasonsParams): Promise<ReasonListResponse> => {
    const response = await api.get('/reasons/', { params });
    return response.data;
  },

  // Get a specific reason by ID
  getReason: async (reasonId: string): Promise<RedactionReason> => {
    const response = await api.get(`/reasons/${reasonId}`);
    return response.data;
  },

  // Update a reason
  updateReason: async (reasonId: string, data: UpdateReasonRequest): Promise<RedactionReason> => {
    const response = await api.put(`/reasons/${reasonId}`, data);
    return response.data;
  },

  // Delete a reason (soft delete)
  deleteReason: async (reasonId: string): Promise<void> => {
    await api.delete(`/reasons/${reasonId}`);
  },
};

// Generic API helper functions
export const apiHelpers = {
  // Handle API errors consistently
  handleError: (error: any): string => {
    if (error.response?.data?.detail) {
      return error.response.data.detail;
    }
    if (error.response?.data?.message) {
      return error.response.data.message;
    }
    if (error.message) {
      return error.message;
    }
    return 'An unexpected error occurred';
  },

  // Check if error is a network error
  isNetworkError: (error: any): boolean => {
    return !error.response && error.request;
  },

  // Check if error is a server error (5xx)
  isServerError: (error: any): boolean => {
    return error.response?.status >= 500;
  },

  // Check if error is a client error (4xx)
  isClientError: (error: any): boolean => {
    return error.response?.status >= 400 && error.response?.status < 500;
  },

  // Check if error is unauthorized (401)
  isUnauthorized: (error: any): boolean => {
    return error.response?.status === 401;
  },

  // Check if error is forbidden (403)
  isForbidden: (error: any): boolean => {
    return error.response?.status === 403;
  },

  // Check if error is not found (404)
  isNotFound: (error: any): boolean => {
    return error.response?.status === 404;
  },
};

// =============================================================================
// PST (Outlook Personal Storage Table) API
// =============================================================================

import type {
  PSTStructure,
  PSTMessageList,
  PSTMessage,
  PSTEntityLocation,
  PSTBatchRedactionResponse,
  PSTExtractionResult,
} from '../types';

// PST API endpoints
export const pstApi = {
  // Get PST file structure (folders and message counts)
  getStructure: async (documentId: string): Promise<PSTStructure> => {
    const response = await api.get(`/pst/${documentId}/structure`);
    return response.data;
  },

  // Get message headers (lazy loading - paginated)
  getMessages: async (
    documentId: string,
    offset: number = 0,
    limit: number = 50
  ): Promise<PSTMessageList> => {
    const response = await api.get(`/pst/${documentId}/messages`, {
      params: { offset, limit },
    });
    return response.data;
  },

  // Get full message content (on-demand loading)
  getMessage: async (documentId: string, messageIndex: number): Promise<PSTMessage> => {
    const response = await api.get(`/pst/${documentId}/messages/${messageIndex}`);
    return response.data;
  },

  // Download attachment from a message
  downloadAttachment: async (
    documentId: string,
    messageIndex: number,
    attachmentIndex: number
  ): Promise<Blob> => {
    const response = await api.get(
      `/pst/${documentId}/messages/${messageIndex}/attachments/${attachmentIndex}`,
      { responseType: 'blob' }
    );
    return response.data;
  },

  // Apply batch redactions and export
  applyBatchRedactions: async (
    documentId: string,
    entities: PSTEntityLocation[]
  ): Promise<PSTBatchRedactionResponse> => {
    const response = await api.post(`/pst/${documentId}/batch-redact`, { entities });
    return response.data;
  },

  // Extract all text for entity detection
  extractText: async (documentId: string): Promise<PSTExtractionResult> => {
    const response = await api.get(`/pst/${documentId}/extract-text`);
    return response.data;
  },

  // Get children completion status for PST container
  getChildrenStatus: async (documentId: string): Promise<{
    total_children: number;
    completed_children: number;
    all_completed: boolean;
    children_status: Array<{ id: string; name: string; status: string }>;
  }> => {
    const response = await api.get(`/pst/${documentId}/children-status`);
    return response.data;
  },

  // Finalize PST container - create ZIP of redacted children
  finalize: async (documentId: string): Promise<{
    status: string;
    message: string;
    zip_document_id?: string;
    children_processed: number;
  }> => {
    const response = await api.post(`/pst/${documentId}/finalize`);
    return response.data;
  },
};

export default api;